import { useGetSearchParamsObj } from '@/src/hooks/srp/searchParams'
import { FlightCard, JsonsData, Sectors } from '@/src/types/flight'
import { useCallback, useState } from 'react'

export interface NearbyAirportPopupData {
    airlineName: string
    departureTime: string
    arrivalTime: string
    displayAirportName: string
    flight: FlightCard
}

interface UseNearByAirportAlertManagerParams {
    onContinue: (flight: FlightCard) => Promise<void>
    jsons: JsonsData | null
    sectors: Sectors | null
    selectedJourneyLeg?: number // 0 for onward, 1 for return (for RT only)
    handleFlightDetailsClose?: () => void // Optional callback to close flight details if nearby airport popup is triggered from there
}

const useNearByAirportAlertManager = ({
    onContinue,
    jsons,
    sectors,
    selectedJourneyLeg = 0,
    handleFlightDetailsClose,
}: UseNearByAirportAlertManagerParams) => {
    const [popupData, setPopupData] = useState<NearbyAirportPopupData | null>(null)
    const { urlQueryObj } = useGetSearchParamsObj()

    // Helper function to check if flight uses nearby airport
    const isNearbyAirportFlight = useCallback(
        (flight: FlightCard) => {
            if (!sectors) return false

            // For return leg (selectedJourneyLeg === 1), swap from/to to match the reverse journey
            const isReturnLeg = selectedJourneyLeg === 1
            const fromAirport = isReturnLeg ? urlQueryObj.to : urlQueryObj.from
            const toAirport = isReturnLeg ? urlQueryObj.from : urlQueryObj.to

            const sectorKeys = flight.sectorKeys[0]
            const sec = sectors?.[sectorKeys]

            if (!sec?.firstDeparture?.airportCode || !sec?.lastArrival?.airportCode) {
                return false
            }

            const isDepartureNearby = fromAirport && fromAirport !== sec.firstDeparture.airportCode
            const isArrivalNearby = toAirport && toAirport !== sec.lastArrival.airportCode

            return isDepartureNearby || isArrivalNearby
        },
        [urlQueryObj, sectors, selectedJourneyLeg]
    )

    const openNearbyAirportPopup = useCallback(
        (flight: FlightCard) => {
            if (!jsons || !sectors) return

            // For return leg, swap from/to
            const isReturnLeg = selectedJourneyLeg === 1
            const fromAirport = isReturnLeg ? urlQueryObj.to : urlQueryObj.from

            const sectorKeys = flight.sectorKeys[0]
            const sec = sectors?.[sectorKeys]

            // Determine nearby airport code
            const nbCode =
                fromAirport && sec?.firstDeparture?.airportCode && fromAirport !== sec.firstDeparture.airportCode
                    ? sec.firstDeparture.airportCode
                    : sec?.lastArrival?.airportCode || ''

            const nbAirportName = jsons?.ap?.[nbCode]?.n || ''
            const displayAirportName = nbAirportName ? `${nbAirportName} (${nbCode})` : nbCode || ''

            const airlineCode = flight?.airlineCodes?.[0]
            const airlineName = airlineCode ? jsons?.airline_names?.[airlineCode] || airlineCode : ''
            const departureTime = flight?.firstDeparture?.time || ''
            const arrivalTime = flight?.lastArrival?.time || ''

            setPopupData({
                airlineName,
                departureTime,
                arrivalTime,
                displayAirportName,
                flight
            })
        },
        [urlQueryObj, jsons, sectors, selectedJourneyLeg]
    )

    const handleNearbyPopupClose = useCallback(() => {
        setPopupData(null)
    }, [])

    // Main function to handle flight selection with nearby airport check
    const handleFlightSelection = useCallback(
        (flight: FlightCard, isRountripFlightOpened: boolean = false) => {
            if (!isRountripFlightOpened && isNearbyAirportFlight(flight)) {
                handleFlightDetailsClose?.()
                openNearbyAirportPopup(flight)
            } else {
                onContinue(flight)
            }
        },
        [isNearbyAirportFlight, openNearbyAirportPopup, onContinue]
    )

    const handleNearbyPopupContinue = useCallback(() => {
        if (popupData?.flight) {
            onContinue(popupData.flight)
            setPopupData(null)
        }
    }, [onContinue, popupData])

    return {
        handleFlightSelection,
        handleNearbyPopupContinue,
        handleNearbyPopupClose,
        isOpen: !!popupData,
        popupData
    }
}

export default useNearByAirportAlertManager
